import Typography from '@mui/material/Typography';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField.tsx';
import type { RadioOptionType } from '@openx/components-core/lib/RadioField/types.ts';
import { useTransferListField } from '@openx/components-core/lib/SearchTransferList/hooks/useTransferListField.ts';
import { SeatTransferList } from '@openx/components-core/lib/SearchTransferList/variants/SeatTransferList/SeatTransferList.tsx';
import { isOptionEqualById } from '@openx/utils/lib/isOptionEqualById.ts';
import { useCallback, useMemo } from 'react';
import { type Path, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDemandPartnerOptionsFetch } from '../../hooks/useDemandPartnerOptionsFetch.ts';
import { curatedDealBuyerDiscountSeatTransferListLocalStorageKey } from '../constants.ts';
import {
  type CuratedDealBuyerDiscountApplyTo,
  type CuratedDealBuyerDiscountSeat,
  type CuratedDealBuyerDiscountsFormData,
  curatedDealBuyerDiscountApplyTo,
} from '../types.ts';
import { useSeatTransferListBulkValidate } from './hooks/useSeatTransferListBulkValidate.ts';
import { useSeatTransferListFields } from './hooks/useSeatTransferListFields.tsx';
import { useSeatTransferListItemsFetch } from './hooks/useSeatTransferListItemsFetch.ts';

type ApplyDiscountToSectionProps = {
  isEdit: boolean;
  seatIds?: Record<string, true> | null;
};

export const ApplyDiscountToSection = ({ isEdit, seatIds }: ApplyDiscountToSectionProps) => {
  const { error, isSubmitting, items, setItems } = useTransferListField<CuratedDealBuyerDiscountsFormData, 'seat_ids'>({
    name: 'seat_ids',
  });

  const { t } = useTranslation();
  const observableApplyTo = useWatch<CuratedDealBuyerDiscountsFormData, 'apply_to'>({ name: 'apply_to' });

  const isAppliedToDemandPartner = observableApplyTo === curatedDealBuyerDiscountApplyTo.DemandPartner;
  const isAppliedToSeats = observableApplyTo === curatedDealBuyerDiscountApplyTo.Seats;

  const { isLoading, list } = useDemandPartnerOptionsFetch(!isAppliedToDemandPartner);

  const isItemBlocked = useCallback(
    ({ id, used_by_curateddealbuyerdiscount }: CuratedDealBuyerDiscountSeat) => {
      if (!used_by_curateddealbuyerdiscount || seatIds?.[id]) {
        return false;
      }

      return t('This Seat is already assigned to another Discount');
    },
    [seatIds, t]
  );

  const discountApplicationOptions = useMemo<RadioOptionType<CuratedDealBuyerDiscountApplyTo>[]>(
    () => [
      {
        dataTest: 'apply-to-demand-partner',
        label: t('Demand Partner'),
        value: curatedDealBuyerDiscountApplyTo.DemandPartner,
      },
      {
        dataTest: 'apply-to-seats',
        label: t('Seats'),
        value: curatedDealBuyerDiscountApplyTo.Seats,
      },
    ],
    [t]
  );

  return (
    <FormSection
      description={t('Select DSP Partner or Seats from the list to assign them to the Discount.')}
      rightColumnSize={isAppliedToDemandPartner ? 5 : undefined}
      title={t('Apply Discount to')}
    >
      <RHFRadioField
        aria-label={t('Apply discount to')}
        isDisabled={isEdit}
        name={'apply_to' satisfies Path<CuratedDealBuyerDiscountsFormData>}
        options={discountApplicationOptions}
        row
        sx={{ mb: 3 }}
      />

      {isAppliedToDemandPartner && (
        <>
          <Typography variant="h3">{t('Demand Side Partner')}</Typography>

          <RHFAutoCompleteVirtualize
            disableClearable
            disabled={isEdit}
            fullWidth
            getOptionLabel={option => option.name}
            isOptionEqualToValue={isOptionEqualById}
            loading={isLoading}
            name={'demand_partner' satisfies Path<CuratedDealBuyerDiscountsFormData>}
            options={list}
            textFieldProps={{
              label: t('DSP'),
              margin: 'dense',
              placeholder: t('[ Select DSP ]'),
            }}
          />
        </>
      )}

      {isAppliedToSeats && (
        <SeatTransferList
          disableSort
          error={error}
          isItemBlocked={isItemBlocked}
          isSubmitting={isSubmitting}
          localStorageKey={curatedDealBuyerDiscountSeatTransferListLocalStorageKey}
          rightItems={items}
          setRightItems={setItems}
          useBulkValidate={useSeatTransferListBulkValidate}
          useFieldsConfig={useSeatTransferListFields}
          useItemsFetch={useSeatTransferListItemsFetch}
        />
      )}
    </FormSection>
  );
};
